import { AnalogGatewayPort } from '@reliance/types/models/AnalogGatewayPort';
import { FacilityAnalogGateway } from '@reliance/types/models/FacilityAnalogGateway';
import { FacilityNumber } from '@reliance/types/models/FacilityNumber';
import { Station } from '@reliance/types/models/Station';
import { useAsync, type UseAsyncReturn } from 'react-async-hook';
import api from '../common/api';
import { PaginatedResponse } from '@reliance/types/responses/PaginatedResponse';

export type FacilityAnalogGatewayModalLookups = {
  stations: Station[];
  facilityNumbers: FacilityNumber[];
};

/**
 * Loads stations and facility numbers for the analog gateway add/edit modal
 */
export function useFacilityAnalogGatewayModalLookups(
  facilityId: number,
  enabled: boolean
): UseAsyncReturn<FacilityAnalogGatewayModalLookups, [number, boolean]> {
  return useAsync(
    async (): Promise<FacilityAnalogGatewayModalLookups> => {
      if (!enabled) {
        return { stations: [], facilityNumbers: [] };
      }
      const [stationsRes, numbersRes] = await Promise.all([
        api.get<Station[]>('/station', {
          params: {
            facilityId,
            status: 'Active',
            types: ['Phone', 'Audio Visit'],
          },
        }),
        api.get<FacilityNumber[]>(`/facility/${facilityId}/number`, {
          params: { active: true },
        }),
      ]);
      return {
        stations: stationsRes.data ?? [],
        facilityNumbers: numbersRes.data ?? [],
      };
    },
    [facilityId, enabled]
  );
}

export type FacilityAnalogGatewayRow = FacilityAnalogGateway & {
  fxoPorts: AnalogGatewayPort[];
  fxsPorts: AnalogGatewayPort[];
};

/**
 * Loads all analog gateways for a facility
 */
export function useFacilityAnalogGatewaysList(
  facilityId: number | undefined
): UseAsyncReturn<FacilityAnalogGatewayRow[], [number | undefined]> {
  return useAsync(
    async (): Promise<FacilityAnalogGatewayRow[]> => {
      if (facilityId == null) {
        return [];
      }
      const res = await api.get<PaginatedResponse<FacilityAnalogGateway>>(
        '/facility-analog-gateway',
        { params: { page: 1, facilityId } }
      );
      const rows = res.data.data ?? [];
      return rows.map((d) => ({
        ...d,
        fxoPorts: (d.analogGatewayPorts ?? []).filter((p) => p.type === 'FXO'),
        fxsPorts: (d.analogGatewayPorts ?? []).filter((p) => p.type === 'FXS'),
      }));
    },
    [facilityId]
  );
}
